/**
 * One asset: its SEO/accessibility fields, its usage, and every write action
 * the API offers (edit, replace bytes, rebuild renditions, delete).
 *
 * The delete path is the interesting one. `DELETE` answers 409 with the list of
 * records still pointing at the image; that is shown inline with a "delete
 * anyway" escape hatch rather than being flattened into a toast, because
 * "cannot delete" without "here is what would break" is a dead end.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { Badge, Button, Input, Select } from '@/components/common';
import { cn, formatDateTime } from '@/utils';
import {
  formatBytes,
  formatDimensions,
  type AssetConfig,
  type AssetInUseConflict,
  type MediaAsset,
  type MediaFolder,
} from '@/services/assets';
import {
  useAssetUsage,
  useDeleteAsset,
  useRegenerateVariants,
  useReplaceAsset,
  useUpdateAssetMetadata,
} from '@/hooks/useAssets';

import { AssetThumb } from './AssetThumb';
import { AssetUsageList } from './AssetUsageList';
import { MediaDialog } from './MediaDialog';

interface AssetDetailModalProps {
  asset: MediaAsset;
  isOpen: boolean;
  onClose: () => void;
  folders: MediaFolder[];
  config?: AssetConfig | null;
  onEditImage: (asset: MediaAsset) => void;
  onDeleted?: (id: string) => void;
}

interface MetaForm {
  alt_text: string;
  title: string;
  caption: string;
  description: string;
  tags: string;
  folder: string;
}

function toForm(asset: MediaAsset): MetaForm {
  return {
    alt_text: asset.alt_text || '',
    title: asset.title || '',
    caption: asset.caption || '',
    description: asset.description || '',
    tags: (asset.tags || []).join(', '),
    folder: asset.folder || '',
  };
}

export function AssetDetailModal({
  asset,
  isOpen,
  onClose,
  folders,
  config,
  onEditImage,
  onDeleted,
}: AssetDetailModalProps) {
  const [form, setForm] = useState<MetaForm>(() => toForm(asset));
  const [conflict, setConflict] = useState<AssetInUseConflict | null>(null);
  const [replaceProgress, setReplaceProgress] = useState<number | null>(null);
  const replaceInput = useRef<HTMLInputElement>(null);

  const usage = useAssetUsage(asset.id, isOpen);
  const updateMetadata = useUpdateAssetMetadata();
  const replaceAsset = useReplaceAsset();
  const regenerate = useRegenerateVariants();
  const deleteAsset = useDeleteAsset(setConflict);

  // Re-seed only on a different asset or a genuine server-side change.
  //
  // NOT on `asset` identity: the list query hands back fresh objects on every
  // background refetch (React Query refetches on window focus), and re-seeding
  // from those would wipe half-typed alt text the moment the user tabbed away
  // and back.
  const assetId = asset.id;
  const assetUpdatedAt = asset.updated_at;
  useEffect(() => {
    setForm(toForm(asset));
    setConflict(null);
    // `asset` is deliberately absent from the deps — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, assetUpdatedAt]);

  const dirty = useMemo(() => {
    const current = toForm(asset);
    return (Object.keys(current) as (keyof MetaForm)[]).some((key) => current[key] !== form[key]);
  }, [asset, form]);

  const folderOptions = useMemo(
    () => [
      { value: '', label: 'No folder' },
      ...folders.map((folder) => ({ value: folder.id, label: folder.path })),
    ],
    [folders],
  );

  const set = (key: keyof MetaForm, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = () => {
    updateMetadata.mutate({
      id: asset.id,
      data: {
        alt_text: form.alt_text,
        title: form.title,
        caption: form.caption,
        description: form.description,
        tags: form.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        folder: form.folder || null,
      },
    });
  };

  const copy = async (path: string, label: string) => {
    const absolute = path.startsWith('http') ? path : `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(absolute);
      toast.success(`${label} copied`);
    } catch {
      // Clipboard access is blocked on insecure origins and in some embedded
      // browsers; showing the URL is more useful than a bare failure toast.
      toast.error(`Copy blocked by the browser. The URL is ${absolute}`);
    }
  };

  const onReplaceFile = (file: File) => {
    setReplaceProgress(0);
    replaceAsset.mutate(
      {
        id: asset.id,
        data: {
          file,
          // Only relevant when the new file is a different format. Sending it
          // always would let a stray JPEG silently move a PNG's URL.
          allow_format_change: false,
        },
        onProgress: setReplaceProgress,
      },
      { onSettled: () => setReplaceProgress(null) },
    );
  };

  const busy = updateMetadata.isPending || replaceAsset.isPending || deleteAsset.isPending;

  return (
    <MediaDialog
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      title={asset.title || asset.original_filename}
      description={`${formatDimensions(asset)} · ${formatBytes(asset.bytes)} · ${asset.extension.toUpperCase()}`}
      closeOnOverlayClick={!busy}
      footer={
        <>
          {asset.can_edit && (
            <Button
              variant="danger"
              size="sm"
              className="mr-auto"
              leftIcon={<TrashIcon className="h-4 w-4" />}
              isLoading={deleteAsset.isPending}
              onClick={() =>
                deleteAsset.mutate(
                  { id: asset.id },
                  // A 409 is handled by `setConflict` (the hook's onConflict),
                  // and onSuccess does not run in that case — so closing here
                  // only ever happens on a real delete.
                  {
                    onSuccess: () => {
                      onDeleted?.(asset.id);
                      onClose();
                    },
                  },
                )
              }
            >
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={save}
            isLoading={updateMetadata.isPending}
            disabled={!dirty || !asset.can_edit}
          >
            Save details
          </Button>
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        {/* --- preview + file facts --- */}
        <div className="space-y-4">
          <div className="aspect-[4/3] overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
            <AssetThumb asset={asset} full sizes="380px" />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<PencilSquareIcon className="h-4 w-4" />}
              onClick={() => onEditImage(asset)}
              disabled={asset.render_hint === 'img-only'}
              title={
                asset.render_hint === 'img-only'
                  ? 'Vectors are edited in a drawing tool, not here'
                  : undefined
              }
            >
              Edit image
            </Button>
            {asset.can_edit && (
              <Button
                variant="outline"
                size="sm"
                leftIcon={<ArrowUpTrayIcon className="h-4 w-4" />}
                onClick={() => replaceInput.current?.click()}
                isLoading={replaceAsset.isPending}
              >
                Replace file
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ClipboardDocumentIcon className="h-4 w-4" />}
              onClick={() => copy(asset.stable_url, 'Permanent link')}
            >
              Copy link
            </Button>
            {asset.can_edit && asset.kind === 'raster' && (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<ArrowPathIcon className="h-4 w-4" />}
                onClick={() => regenerate.mutate(asset.id)}
                isLoading={regenerate.isPending}
                title="Rebuild the responsive and retina renditions"
              >
                Rebuild sizes
              </Button>
            )}
            <input
              ref={replaceInput}
              type="file"
              className="sr-only"
              accept={config?.accepted_mime_types?.join(',') || 'image/*'}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onReplaceFile(file);
                event.target.value = '';
              }}
            />
          </div>

          {replaceProgress !== null && (
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div
                className="h-full rounded-full bg-primary-600 transition-all"
                style={{ width: `${replaceProgress}%` }}
              />
            </div>
          )}

          <dl className="space-y-1.5 text-sm">
            <Fact label="File name" value={asset.original_filename} />
            <Fact label="Dimensions" value={formatDimensions(asset)} />
            <Fact
              label="Size"
              value={
                asset.original_bytes && asset.original_bytes !== asset.bytes
                  ? `${formatBytes(asset.bytes)} (was ${formatBytes(asset.original_bytes)})`
                  : formatBytes(asset.bytes)
              }
            />
            <Fact label="Format" value={`${asset.extension.toUpperCase()} · ${asset.mime_type}`} />
            <Fact label="Library" value={asset.is_platform ? 'Shared platform library' : asset.broker_name || 'This account'} />
            <Fact label="Uploaded" value={`${formatDateTime(asset.created_at)}${asset.uploaded_by_name ? ` by ${asset.uploaded_by_name}` : ''}`} />
            {asset.replace_count > 0 && (
              <Fact
                label="Replaced"
                value={`${asset.replace_count} time${asset.replace_count === 1 ? '' : 's'}, last ${formatDateTime(asset.replaced_at)}`}
              />
            )}
            <Fact label="Renditions" value={`${asset.variants.length} generated`} />
          </dl>

          <div className="flex flex-wrap gap-1.5">
            {asset.has_alpha && <Badge variant="info" size="sm">Transparent</Badge>}
            {asset.is_animated && <Badge variant="warning" size="sm">Animated</Badge>}
            {asset.kind === 'vector' && <Badge variant="primary" size="sm">Vector</Badge>}
            {typeof asset.processing?.saved_bytes === 'number' && asset.processing.saved_bytes > 0 && (
              <Badge variant="success" size="sm">
                Optimised — saved {formatBytes(asset.processing.saved_bytes)}
              </Badge>
            )}
          </div>
        </div>

        {/* --- metadata + usage --- */}
        <div className="space-y-5">
          <section>
            <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
              Accessibility &amp; SEO
            </h4>
            <div className="space-y-3">
              <Labelled
                label="Alt text"
                hint="What the image shows, for screen readers and search engines. Leave empty only if the image is purely decorative."
                warn={!form.alt_text}
              >
                <Input
                  value={form.alt_text}
                  maxLength={300}
                  onChange={(event) => set('alt_text', event.target.value)}
                  placeholder="Star Health company logo"
                  className="dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                  disabled={!asset.can_edit}
                />
              </Labelled>

              <Labelled label="Title" hint="The name this image is listed under.">
                <Input
                  value={form.title}
                  maxLength={200}
                  onChange={(event) => set('title', event.target.value)}
                  className="dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                  disabled={!asset.can_edit}
                />
              </Labelled>

              <Labelled label="Caption" hint="Shown under the image where a section supports one.">
                <Input
                  value={form.caption}
                  maxLength={500}
                  onChange={(event) => set('caption', event.target.value)}
                  className="dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                  disabled={!asset.can_edit}
                />
              </Labelled>

              <Labelled label="Description" hint="Longer notes — where this came from, when to use it.">
                <textarea
                  value={form.description}
                  rows={3}
                  onChange={(event) => set('description', event.target.value)}
                  disabled={!asset.can_edit}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:disabled:bg-gray-800"
                />
              </Labelled>

              <div className="grid gap-3 sm:grid-cols-2">
                <Labelled label="Tags" hint="Comma separated. Used by the library search.">
                  <Input
                    value={form.tags}
                    onChange={(event) => set('tags', event.target.value)}
                    placeholder="logo, health, hero"
                    className="dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                    disabled={!asset.can_edit}
                  />
                </Labelled>
                <Labelled label="Folder">
                  <Select
                    options={folderOptions}
                    value={form.folder}
                    onChange={(event) => set('folder', event.target.value)}
                    className="dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                    disabled={!asset.can_edit}
                  />
                </Labelled>
              </div>
            </div>
          </section>

          <section>
            <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
              Used in {usage.data ? `${usage.data.count} place${usage.data.count === 1 ? '' : 's'}` : '…'}
            </h4>
            <AssetUsageList usages={usage.data?.usages || []} isLoading={usage.isLoading} />
          </section>

          {conflict && (
            <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/50 dark:bg-amber-900/20">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                {conflict.detail}
              </p>
              <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                Deleting it anyway leaves a broken image on everything listed below. Replacing the file
                instead keeps every one of these working.
              </p>
              <div className="mt-3">
                <AssetUsageList usages={conflict.usages} />
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setConflict(null)}>
                  Keep it
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  isLoading={deleteAsset.isPending}
                  onClick={() =>
                    deleteAsset.mutate(
                      { id: asset.id, force: true },
                      {
                        onSuccess: () => {
                          setConflict(null);
                          onDeleted?.(asset.id);
                          onClose();
                        },
                      },
                    )
                  }
                >
                  Delete anyway
                </Button>
              </div>
            </section>
          )}
        </div>
      </div>
    </MediaDialog>
  );
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="truncate text-right text-gray-900 dark:text-gray-200">{value}</dd>
    </div>
  );
}

function Labelled({
  label,
  hint,
  warn,
  children,
}: {
  label: string;
  hint?: string;
  warn?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        className={cn(
          'mb-1 block text-sm font-medium',
          warn ? 'text-amber-700 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300',
        )}
      >
        {label}
        {warn && <span className="ml-2 text-xs font-normal">· missing</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
    </div>
  );
}
